import React, { Fragment } from 'react';
import tw from 'twin.macro';
import { Dialog } from '@headlessui/react';

import Close from '~components/Svg/Close';
import { Transition } from '~components/Transition';

import Collapse from './Collapse';
import NavLink from './NavLink';

interface ModalProps {
  open: boolean;
  onClose: () => void;
}

const Modal: React.FC<ModalProps> = ({ open, onClose }) => {
  const initialFocus = React.useRef(null);
  return (
    <Transition show={open} as={Fragment}>
      <Dialog
        open={open}
        onClose={onClose}
        initialFocus={initialFocus}
        tw="fixed max-h-screen inset-0 z-40 overflow-x-hidden overflow-y-scroll"
      >
        <button
          css={[
            tw`flex space-x-2 items-center rounded-lg text-lg font-bold p-2 fixed top-3 right-5 z-50`,
            tw`transition-colors duration-100 ease`,
            tw`focus:(outline-none ring-4 ring-green)`,
            tw`hover:(bg-blue-900 text-blue-50)`,
            tw`bg-white text-blue`
          ]}
          ref={initialFocus}
          onClick={onClose}
        >
          <span>
            Close <span tw="sr-only">Menu</span>
          </span>
          <Close tw="w-4" />
        </button>
        <div tw="flex flex-col items-center py-24 max-w-6xl relative z-20 mx-auto">
          <NavLink to="/">Home</NavLink>
          <NavLink to="/leadership-report">Full Leadership Report</NavLink>
          <Collapse
            title="Services &amp; Programs"
            items={[
              {
                title: 'Programs & Outcomes',
                to: '/programs-outcomes'
              },
              {
                title: 'Community Development',
                to: '/community-development'
              },
              {
                title: 'Independent Living & Seniors',
                to: '/independent-living-seniors'
              },
              {
                title: 'Child Care',
                to: '/childcare'
              },
              {
                title: 'Employment Services',
                to: '/employment-services'
              },
              {
                title: 'Newcomer Services',
                to: '/newcomer-services'
              },
              {
                title: 'Children & Youth',
                to: '/children-youth'
              },
              {
                title: 'Housing & Housing Development',
                to: '/housing-development'
              },
              {
                title: 'Urban Health & Homelessness Services',
                to: '/urban-health-homelessness-services'
              },
              {
                title: 'Conflict Resolution & Training',
                to: '/conflict-resolution-training'
              }
            ]}
          />
          <NavLink to="/donors">Donors</NavLink>
          <NavLink to="/volunteers">Volunteers</NavLink>
          <NavLink to="/financials">Financials</NavLink>
          <NavLink to="/foundation">The Neighbourhood Group Foundation</NavLink>
          <NavLink to="/neighbourhood-link-homes">Neighbourhood Link Homes</NavLink>
          <NavLink to="/in-memoriam-liane-regendanz">In Memoriam: Liane Regendanz</NavLink>
          <NavLink to="/board-directors">Board of Directors</NavLink>
        </div>
        <Transition.Child
          as={Fragment}
          enter={tw`ease-out duration-300`}
          enterFrom={tw`opacity-0`}
          enterTo={tw`opacity-100`}
          leave={tw`ease-in duration-300`}
          leaveFrom={tw`opacity-100`}
          leaveTo={tw`opacity-0`}
        >
          <Dialog.Overlay tw="fixed inset-0 bg-blue z-10" />
        </Transition.Child>
      </Dialog>
    </Transition>
  );
};

export default Modal;
